// The three pages of CAPTAIN (round 091 card 05, R1889; ui-map-interface.md
// §12 "CAPTAIN"): WARDROOM and RECRUITMENT as captain rows of `caprow.ts`,
// and the Brig of PRISON. Each page is a pure function of the state and of
// the readers the overlay hands it.

import { Building } from '../../gen/proto/aquaphobia';
import { overlay as S } from '../../gen/strings';
import { type GameState, dur, num } from '../../state';
import { armFaces } from '../choice/choice';
import { esc } from '../esc';
import { withResIcons } from '../resicon';
import { captainActions, captainHeadHtml, captainRowHtml, type CapRowCtx } from './caprow';
import { emptyGoHtml } from './howfold';

/**
 * CAPTAIN · WARDROOM (round 091 card 05, R1889): the head with the
 * Academy aside, the column head of the strip, and one captain row per
 * roster captain with ⋯ (one TRAIN press per ability, then RELEASE; or
 * PAY, R1901). Training starts from the TRAIN presses of ⋯ and of the
 * captain card, which the name opens; the TRAINING page is gone.
 * `moreOpen` is the captain whose ⋯ stands open, or 0; `armed` reads the
 * overlay's arm gate (R696).
 */
export function wardroomHtml(ctx: CapRowCtx, moreOpen: number, armed: (key: string) => boolean): string {
  const s = ctx.state;
  const e = s.empire;
  if (!e) return '';
  const academy = s.level(Building.BUILDING_ACADEMY);
  // The aside names the Academy level alone: training needs no Academy
  // (R3269), so an empire with none shows no aside.
  let html = `<h3>${S.wardroom_head({ n: s.roster.length, cap: e.captainSlots })}`
    + `${academy > 0 ? ` · ${S.wardroom_academy({ level: academy })}` : ''}</h3>`;
  if (s.roster.length === 0) html += `<div class="empty">${S.wardroom_empty}</div>`;
  else html += captainHeadHtml({ more: true, names: s.catalog?.attrs, open: ctx.codesOpen });
  for (const c of s.roster) {
    html += captainRowHtml(ctx, c, {
      more: captainActions(s, c, armed), moreOpen: moreOpen === c.id,
    });
  }
  return html;
}

/**
 * CAPTAIN · RECRUITMENT: the cadets on offer as captain rows (R1889),
 * each with RECRUIT at the right edge. PASS stands on the captain card,
 * with one press (R696 rule 3), so the row keeps room for the name on a
 * telephone.
 */
export function recruitmentHtml(ctx: CapRowCtx): string {
  const s = ctx.state;
  let html = `<h3>${S.recruitment_head}</h3>`;
  if (s.cadets.length === 0) html += `<div class="empty">${S.recruitment_empty}</div>`;
  // The recruiter's drain (R679): a berth that a pass, a recruit or a
  // dismissal emptied fills on its own clock, and the wait is the one
  // figure a short pool cannot explain by itself. The server sends the
  // seconds in world time, so the row prints them with no conversion.
  const wait = s.empire?.nextOfferSec ?? 0;
  if (wait > 0) {
    html += `<div class="row"><span class="muted">`
      + `${S.recruitment_next_offer({ left: dur(wait) })}</span></div>`;
  }
  if (s.cadets.length > 0) html += captainHeadHtml({ press: true, names: s.catalog?.attrs, open: ctx.codesOpen });
  for (const c of s.cadets) {
    html += captainRowHtml(ctx, c, {
      press: { act: 'recruit', id: c.id, face: 'dark', label: S.btn_recruit },
    });
  }
  return html;
}

/**
 * CAPTAIN · PRISON: the captains the Brig holds, each with the time held
 * (R2744) and RELEASE. The
 * head opens the Brig build card, which states the level figures (the
 * cells, the capture range and chance) now and at the next level
 * (R1895); the page keeps no copy of them.
 */
export function prisonHtml(s: GameState, armed: (key: string) => boolean): string {
  const e = s.empire;
  if (!e) return '';
  const brig = e.brig ?? [];
  let html = `<h3><button class="info" data-act="info" data-kind="building"`
    + ` data-id="${Building.BUILDING_PRISON}" title="${S.info_btn_building_title}">${S.brig_head({
      n: brig.length, cells: s.level(Building.BUILDING_PRISON),
    })}</button></h3>`;
  if (s.level(Building.BUILDING_PRISON) === 0) {
    // GO TO scrolls to the Brig row of BUILD · BASE and flashes it (R3263).
    html += emptyGoHtml(S.brig_no_prison, 'BUILD', 'BASE', { kind: 'building', id: Building.BUILDING_PRISON });
  } else if (brig.length === 0) {
    html += `<div class="empty">${S.brig_empty}</div>`;
  }
  for (const pr of brig) {
    // A captain id counts per empire, so two prisoners of two owners can
    // carry one id: the owner's id and the captain id name the prisoner,
    // in the arm key and in the release (R2743). `actButton` reads the
    // part after the colon as the second argument.
    const who = `${pr.captainId}:${pr.ownerId}`;
    const on = armed(`relcap:${who}`);
    // The time held (R2744) is world time, in the form of every other
    // duration of the panel; a capture whose moment the server does not
    // know sends 0 and the line leaves it out.
    const facts = { owner: esc(pr.owner), ransom: num(pr.ransom) };
    const line = pr.heldSec > 0 ? S.brig_prisoner_held({ ...facts, held: dur(pr.heldSec) }) : S.brig_prisoner(facts);
    html += `<div class="row"><span><span class="name">${esc(pr.captain)}</span>`
      + `<span class="sub">${withResIcons(line)}</span></span>`
      // The release gives the prisoner back and the ransom is lost, so
      // it asks for a second press (R696).
      + `<button class="${on ? 'armed' : ''}"`
      + ` data-act="relcap" data-id="${who}" data-arm="1">`
      + `${armFaces(S.btn_release, S.release_armed, on)}</button></div>`;
  }
  return html;
}
